import type { BlotterTableRow, CareOrder, CurrencyConversionRate, ValueOf } from '@talos/kyoko';
import {
  CareOrderStatusEnum,
  isGridApiReady,
  logger,
  SideEnum,
  useCallbackRef,
  useCurrencyConversionRatesValue,
  type UseBlotterTable,
} from '@talos/kyoko';
import Big from 'big.js';
import { isEqual } from 'lodash-es';
import { startTransition, useEffect, useState } from 'react';

export interface CareOrderHUDData {
  isLoading: boolean;
  totalNotionalBuy: Big;
  notionalBuyExecuted: Big;
  notionalBuyOutstanding: Big;
  totalNotionalSell: Big;
  notionalSellExecuted: Big;
  notionalSellOutstanding: Big;
}

export const INITIAL_STATE: CareOrderHUDData = {
  isLoading: false,
  totalNotionalBuy: Big(0),
  notionalBuyExecuted: Big(0),
  notionalBuyOutstanding: Big(0),
  totalNotionalSell: Big(0),
  notionalSellExecuted: Big(0),
  notionalSellOutstanding: Big(0),
};

export function useCareOrderHUDData<TData>(
  blotterTable: UseBlotterTable<TData>,
  getCareOrder: (row: BlotterTableRow<TData>) => CareOrder | undefined
): CareOrderHUDData {
  const [data, setData] = useState<CareOrderHUDData>(INITIAL_STATE);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const rates = useCurrencyConversionRatesValue(currencies, 'USD');

  const { gridApi, getRowsAfterFilter } = blotterTable;

  const updateData = useCallbackRef(() => {
    const careOrders = getRowsAfterFilter()
      .map(row => getCareOrder(row))
      .filter((careOrder): careOrder is CareOrder => careOrder != null);
    const next = getCareOrderHUDCurrencies(careOrders);
    setCurrencies(prev => (isEqual(prev, next) ? prev : next));

    if (rates == null) {
      setData(INITIAL_STATE);
      return;
    }

    startTransition(() => {
      setData(computeCareOrderHUDData(careOrders, rates));
    });
  });

  useEffect(() => {
    updateData();
  }, [rates, updateData]);

  useEffect(() => {
    if (!isGridApiReady(gridApi)) {
      return;
    }
    gridApi.addEventListener('filterChanged', updateData);
    gridApi.addEventListener('asyncTransactionsFlushed', updateData);
    return () => {
      if (gridApi == null || gridApi.isDestroyed()) {
        return;
      }
      gridApi.removeEventListener('filterChanged', updateData);
      gridApi.removeEventListener('asyncTransactionsFlushed', updateData);
    };
  }, [gridApi, updateData, rates]);

  return data;
}

/** Just goes through all the care orders in the blotter rows list and collects all the unique currencies */
export function getCareOrderHUDCurrencies(rows: CareOrder[]): string[] {
  const currencies = new Set<string>();
  for (const row of rows) {
    if (row.LegSummary) {
      row.LegSummary.forEach(ls => currencies.add(ls.Currency));
    } else if (row.Currency) {
      currencies.add(row.Currency);
    }
  }

  // Sort alphabetically so we don't make unnecessary requests just because the ordering of rows in the blotter is different
  return [...currencies].toSorted();
}

export const canceledCareOrderStatuses = new Set<ValueOf<typeof CareOrderStatusEnum>>([
  CareOrderStatusEnum.Canceled,
  CareOrderStatusEnum.FilledAndCanceled,
  CareOrderStatusEnum.Rejected,
]);

interface CareOrderHUDConstituent {
  currency: string | undefined;
  side: SideEnum;
  orderQty: string | undefined;
  cumQty: string | undefined;
  leavesQty: string | undefined;
}

function getCareOrderHUDConstituents(careOrder: CareOrder): CareOrderHUDConstituent[] {
  if (careOrder.LegSummary != null && careOrder.LegSummary.length > 0) {
    return careOrder.LegSummary.map(legSummary => ({
      currency: legSummary.Currency,
      side: legSummary.Side,
      orderQty: legSummary.OrderQty,
      cumQty: legSummary.CumQty,
      leavesQty: legSummary.LeavesQty,
    }));
  }

  return [
    {
      currency: careOrder.Currency,
      side: careOrder.Side,
      orderQty: careOrder.OrderQty,
      cumQty: careOrder.CumQty,
      leavesQty: careOrder.LeavesQty,
    },
  ];
}

export function computeCareOrderHUDData(
  rows: CareOrder[],
  rates: Map<string, CurrencyConversionRate | undefined>
): CareOrderHUDData {
  // Compute HUD data
  let totalNotionalBuy = Big(0);
  let notionalBuyExecuted = Big(0);
  let notionalBuyOutstanding = Big(0);
  let totalNotionalSell = Big(0);
  let notionalSellExecuted = Big(0);
  let notionalSellOutstanding = Big(0);

  for (const row of rows) {
    const careOrder = row;

    if (careOrder.status === CareOrderStatusEnum.Unknown) {
      continue;
    }

    try {
      const constituents = getCareOrderHUDConstituents(careOrder);
      for (const constituent of constituents) {
        if (!constituent.currency) {
          continue;
        }
        const conversionRate = rates.get(constituent.currency);

        // If there's a single row for which we cannot compute the notional,
        // we return the initial state.
        if (conversionRate == null || conversionRate.Rate == null) {
          return INITIAL_STATE;
        }

        if (constituent.side === SideEnum.Buy) {
          if (canceledCareOrderStatuses.has(careOrder.status)) {
            totalNotionalBuy = totalNotionalBuy.plus(Big(conversionRate.Rate).times(constituent.cumQty ?? 0));
          } else {
            totalNotionalBuy = totalNotionalBuy.plus(Big(conversionRate.Rate).times(constituent.orderQty ?? 0));
          }
          notionalBuyExecuted = notionalBuyExecuted.plus(Big(conversionRate.Rate).times(constituent.cumQty ?? 0));
          notionalBuyOutstanding = notionalBuyOutstanding.plus(
            Big(conversionRate.Rate).times(constituent.leavesQty ?? 0)
          );
        } else {
          if (canceledCareOrderStatuses.has(careOrder.status)) {
            totalNotionalSell = totalNotionalSell.plus(Big(conversionRate.Rate).times(constituent.cumQty ?? 0));
          } else {
            totalNotionalSell = totalNotionalSell.plus(Big(conversionRate.Rate).times(constituent.orderQty ?? 0));
          }
          notionalSellExecuted = notionalSellExecuted.plus(Big(conversionRate.Rate).times(constituent.cumQty ?? 0));
          notionalSellOutstanding = notionalSellOutstanding.plus(
            Big(conversionRate.Rate).times(constituent.leavesQty ?? 0)
          );
        }
      }
    } catch (e) {
      logger.error(e as Error);
      return INITIAL_STATE;
    }
  }

  return {
    isLoading: false,
    totalNotionalBuy,
    notionalBuyExecuted,
    notionalBuyOutstanding,
    totalNotionalSell,
    notionalSellExecuted,
    notionalSellOutstanding,
  };
}
